import React, { type FC, useState, useMemo, useCallback, useEffect } from 'react'
import { Select, Input, Switch, Flex, Space, Tree, type TreeDataNode } from 'antd'
import { CheckboxUncheckedRegular, CheckboxCheckedFilled } from '@fluentui/react-icons'

export interface ITreeSelectOption {
  key: string
  title: string
  isLeaf?: boolean
  children?: ITreeSelectOption[]
}

interface ITreeSelectInputProps {
  value?: string | null
  options: ITreeSelectOption[]
  onChange: (value: string | null) => void
  onLoadData?: (node: ITreeSelectOption) => Promise<void>
  placeholder?: string
  inputPlaceholder?: string
  disabled?: boolean
  loading?: boolean
  switchLabels?: {
    list: string
    input: string
  }
}

const findNodeTitle = (options: ITreeSelectOption[], key: string): string | null => {
  for (const option of options) {
    if (option.key === key) {
      return option.title
    }
    if (option.children) {
      const found = findNodeTitle(option.children, key)
      if (found) return found
    }
  }
  return null
}

const findNodeByKey = (options: ITreeSelectOption[], key: string): ITreeSelectOption | null => {
  for (const option of options) {
    if (option.key === key) {
      return option
    }
    /* istanbul ignore next */
    if (option.children) {
      const found = findNodeByKey(option.children, key)
      /* istanbul ignore next */
      if (found) return found
    }
  }
  /* istanbul ignore next -- defensive return, unreachable in normal flow */
  return null
}

interface IModeSwitchProps {
  isInputMode: boolean
  onChange: (checked: boolean) => void
  disabled: boolean
  switchLabels: {
    list: string
    input: string
  }
  className: string
}

const ModeSwitch: FC<IModeSwitchProps> = ({ isInputMode, onChange, disabled, switchLabels, className }) => (
  <Flex align='center' gap={8} className={className}>
    <span className={!isInputMode ? 'tree-select-input__label--active' : ''}>
      {switchLabels.list}
    </span>
    <Switch
      size='small'
      checked={isInputMode}
      onChange={onChange}
      disabled={disabled}
      aria-label='toggle-input-mode'
    />
    <span className={isInputMode ? 'tree-select-input__label--active' : ''}>
      {switchLabels.input}
    </span>
  </Flex>
)

export const TreeSelectInput: FC<ITreeSelectInputProps> = ({
  value,
  options,
  onChange,
  onLoadData,
  placeholder = 'Select an option',
  inputPlaceholder = 'Enter value manually',
  disabled = false,
  loading = false,
  switchLabels = { list: 'List', input: 'Input' }
}) => {
  const [isInputMode, setIsInputMode] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [open, setOpen] = useState(false)

  useEffect(() => {
    setInputValue(value || '')
  }, [value])
  
  const handleModeSwitch = (checked: boolean) => {
    setIsInputMode(checked)
    if (checked) {
      setInputValue(value || '')
    } else {
      onChange(null)
      setInputValue('')
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)
    onChange(newValue || null)
  }

  const handleTreeSelect = (selectedKeys: React.Key[]) => {
    const selectedKey = selectedKeys[0] as string
    onChange(selectedKey || /* istanbul ignore next */ null)
    setOpen(false)
  }

  const renderTreeNode = useCallback((node: ITreeSelectOption): TreeDataNode => {
    const isSelected = value === node.key
    const isLeafNode = node.isLeaf === true
    const hasLoadedChildren = node.children && node.children.length > 0
    const showCheckbox = isLeafNode || (!hasLoadedChildren && node.isLeaf !== false)
    return {
      key: node.key,
      title: (
        <Flex align='center' gap={8} className='tree-select-input__node'>
          {showCheckbox && (
            isSelected
              ? <CheckboxCheckedFilled fontSize='x-large' />
              : <CheckboxUncheckedRegular fontSize='x-large' />
          )}
          <span>{node.title}</span>
        </Flex>
      ),
      selectable: showCheckbox,
      isLeaf: isLeafNode,
      children: node.children?.map(renderTreeNode)
    }
  }, [value])

  const treeData = useMemo(() => options.map(renderTreeNode), [options, renderTreeNode])

  const selectedTitle = useMemo(() => {
    if (!value) return null
    return findNodeTitle(options, value)
  }, [value, options])

  return (
    <div className='tree-select-input'>
      {isInputMode ? (
        <Space.Compact style={{ width: '100%' }}>
          <Input
            value={inputValue}
            onChange={handleInputChange}
            placeholder={inputPlaceholder}
            disabled={disabled}
            aria-label='tree-select-input-field'
          />
          <ModeSwitch
            isInputMode={isInputMode}
            onChange={handleModeSwitch}
            disabled={disabled}
            switchLabels={switchLabels}
            className='tree-select-input__switch-addon'
          />
        </Space.Compact>
      ) : (
        <Select
          style={{ width: '100%' }}
          open={open}
          onOpenChange={setOpen}
          value={value}
          disabled={disabled}
          loading={loading}
          placeholder={placeholder}
          aria-label='tree-select-dropdown'
          popupRender={() => (
            <div className='tree-select-input__dropdown'>
              <div className='tree-select-input__tree-container'>
                {options.length ? (
                  <Tree
                    treeData={treeData}
                    selectedKeys={value ? [value] : []}
                    onSelect={handleTreeSelect}
                    loadData={onLoadData ? (treeNode) => {
                      const originalNode = findNodeByKey(options, treeNode.key as string)
                      /* istanbul ignore next */
                      return originalNode ? onLoadData(originalNode) : Promise.resolve()
                    } : undefined}
                    disabled={disabled}
                    aria-label='tree-select-tree'
                  />
                ) : (
                  <div className='tree-select-input__placeholder'>
                    {loading ? 'Loading...' : 'No items found'}
                  </div>
                )}
              </div>
              <ModeSwitch
                isInputMode={isInputMode}
                onChange={handleModeSwitch}
                disabled={disabled}
                switchLabels={switchLabels}
                className='tree-select-input__switch-container'
              />
            </div>
          )}
          labelRender={() => selectedTitle ? <span>{selectedTitle}</span> : null}
        />
      )}
    </div>
  )
}
